import { useCallback, useEffect, memo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindowF,
} from "@react-google-maps/api";
import { containerStyle, mapStyle } from "../utilities/utilities";

const MapComponent = ({
  isMarkerShown,
  isDataLoaded,
  agencies,               // { agencies: [...] } para bounds
  selectedAgencyId,
  selectedMarkerVersion,
  center,
  setCenter,
  currentLocation,
  idCurrentInfoWindow,
  zoom,
  handleBluexMarkerClick,
  setIdCurrentInfoWindow,
  setAgenciesInsideBoundaries,
  map,
  setMap,
  setCurrentGeolocation,

  filteredAgencies,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_APIKEY,
    language: "es",
    region: "CL",
  });

  const onLoad = useCallback(
    function callback(mapInstance) {
      setMap(mapInstance);
    },
    [setMap]
  );

  const onUnmount = useCallback(
    function callback() {
      setMap(null);
    },
    [setMap]
  );

  const onClickMarker = useCallback(
    (id) => {
      handleBluexMarkerClick(id);
      if (map) {
        const newCenter = map.getCenter();
        setCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
      }
    },
    [map, handleBluexMarkerClick, setCenter]
  );

  const onCloseInfoWindow = useCallback(() => {
  setIdCurrentInfoWindow(-1);
  if (map) {
    const newCenter = map.getCenter();
    setCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
  }
}, [map, setIdCurrentInfoWindow, setCenter]);

  const handleBoundsChanged = () => {
    if (map && agencies && agencies.agencies) {
      const bounds = map.getBounds();
      if (!bounds) return;

      const northeast = bounds.getNorthEast();
      const southwest = bounds.getSouthWest();
      if (!northeast || !southwest) return;

      const agenciesInsideBoundaries = agencies.agencies.filter((agency) => {
        const { latitude, longitude } = agency.address.geolocation;
        return (
          latitude < northeast.lat() &&
          latitude > southwest.lat() &&
          longitude < northeast.lng() &&
          longitude > southwest.lng()
        );
      });

      setAgenciesInsideBoundaries(agenciesInsideBoundaries);

      const newCenter = map.getCenter();
      setCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
    }
  };

  useEffect(() => {
    handleBoundsChanged();
  }, [agencies]);

  const agenciesToRender = isDataLoaded ? filteredAgencies || [] : [];

  return isLoaded ? (
    <div className="position-relative w-100 h-100 map-container">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={{ lat: center.lat, lng: center.lng }}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        id={map}
        options={{
          gestureHandling: "greedy",
          disableDefaultUI: true,
          zoomControl: true,
          styles: mapStyle,
          minZoom: 7,
          maxZoom: 18,
          rotateControl: true,
        }}
        onDragEnd={handleBoundsChanged}
        onZoomChanged={handleBoundsChanged}
      >
        <div className="w-100 h-100 d-flex align-items-end justify-content-end">
          <button
            className="center-button z-index-9999"
            onClick={() => {
              if (map) {
                setCurrentGeolocation();
              }
            }}
          >
            <div className="d-flex align-items-center justify-content-center">
              <img
                src="/assets/location-icon.svg"
                alt="location-icon"
                style={{ width: "24px", height: "24px" }}
              />
            </div>
          </button>
        </div>

        {isMarkerShown && (
          <MarkerF
            position={currentLocation}
            icon={{
              url: process.env.REACT_APP_IMG_URL + "marker_blue_here.png",
              scaledSize: new window.google.maps.Size(51, 60),
            }}
          />
        )}

        {agenciesToRender.map((agency) => {
          const position = {
            lat: agency.address.geolocation.latitude,
            lng: agency.address.geolocation.longitude,
          };

          const isSelected = selectedAgencyId === agency.agencyId;

          const markerKey = isSelected
            ? `${agency.agencyId}-${selectedMarkerVersion}`
            : String(agency.agencyId);

          return (
            <div key={markerKey}>
              <MarkerF
                position={position}
                icon={{
                  url:
                    process.env.REACT_APP_IMG_URL +
                    (agency.typeAgency.name.toLowerCase().includes("copec")
                      ? "marker_copec.1.png"
                      : "marker-bluev2.png"),
                  scaledSize: new window.google.maps.Size(
                    isSelected ? 51 : 34, // ancho
                    isSelected ? 60 : 40 // alto
                  ),
                }}
                onClick={() => onClickMarker(agency.agencyId)}
              />

              {agency.agencyId == idCurrentInfoWindow && (
                <InfoWindowF
                  position={position}
                  onCloseClick={onCloseInfoWindow}
                  options={{
                    pixelOffset: new window.google.maps.Size(0,-60)
                  }}
                >
                  <>
                    <div>
                      <span className="fw-800 fs-20px color-text-blue">
                        {agency.agencyName}
                      </span>
                    </div>
                    <div>
                      <span className="color-text-blue fs-16px font-lato">
                        {`${agency.address.streetName} ${agency.address.streetNumber}, ${agency.address.state.name}`}
                      </span>
                    </div>
                  </>
                </InfoWindowF>
              )}
            </div>
          );
        })}
      </GoogleMap>
    </div>
  ) : (
    <div className="p-4">
      <p className="fs-1">Cargando...</p>
    </div>
  );
};

export default memo(MapComponent);
